import React, { useEffect, useState } from 'react';
import { Drawer, Button, Select, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import * as actions from "store/actions/AuthActions";
import useIsMobile from 'hooks/useIsMobile';

const AddLeadsDrawer = ({ visible, onClose, campaignId, onSuccess, existingLeadIds = [] }) => {
    const isMobile = useIsMobile();
    const [leads, setLeads] = useState([]);
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [memberStatus, setMemberStatus] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (visible) {
            fetchLeads();
        }
    }, [visible]);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const response = await actions.loadLeads(1, '', MAX_PAGE_SIZE);
            if (response.status === 200) {
                // Filter out leads that are already added to this campaign
                const allLeads = response.data.results || [];
                const filteredLeads = allLeads.filter(lead => !existingLeadIds.includes(lead.id));
                setLeads(filteredLeads);
            }
        } catch (error) {
            message.error('Failed to load leads');
        }
        setLoading(false);
    };

    const handleAddLeads = async () => {
        if (selectedLeads.length === 0) {
            message.warning('Please select at least one lead');
            return;
        }

        setSubmitting(true);
        try {
            for (let leadId of selectedLeads) {
                const data = {
                    campaign: campaignId,
                    lead: leadId,
                    member_status: memberStatus[leadId] || 'NONE'
                };
                await actions.addCampaignLead(data);
            }
            message.success(`${selectedLeads.length} lead(s) added successfully`);
            setSelectedLeads([]);
            setMemberStatus({});
            onSuccess();
            onClose();
        } catch (error) {
            message.error('Failed to add leads');
        }
        setSubmitting(false);
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => `${record.first_name || ''} ${record.last_name || ''}`.trim(),
        },
        {
            title: 'Company',
            dataIndex: 'company_name',
            key: 'company_name',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone_number',
        },
        {
            title: 'Member Status',
            key: 'member_status',
            render: (_, record) => (
                <Select
                    style={{ width: 150 }}
                    value={memberStatus[record.id] || 'NONE'}
                    onChange={(value) => setMemberStatus({ ...memberStatus, [record.id]: value })}
                    disabled={!selectedLeads.includes(record.id)}
                >
                    <Select.Option value="NONE">-None-</Select.Option>
                    <Select.Option value="PLANNING">Planning</Select.Option>
                    <Select.Option value="ACTIVE">Active</Select.Option>
                    <Select.Option value="INACTIVE">Inactive</Select.Option>
                    <Select.Option value="COMPLETE">Complete</Select.Option>
                </Select>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys: selectedLeads,
        onChange: (selectedRowKeys) => {
            setSelectedLeads(selectedRowKeys);
        },
    };

    return (
        <Drawer
            title="Add Leads to Campaign"
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 800}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={handleAddLeads}
                        loading={submitting}
                        style={isMobile ? { flex: 1 } : undefined}
                    >
                        {isMobile ? "Add Leads" : "Add Selected Leads"}
                    </Button>
                </div>
            }
        >
            <Table
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={leads}
                loading={loading}
                rowKey="id"
                pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                size="small"
            />
        </Drawer>
    );
};

export default AddLeadsDrawer;
